<template>
  <NuxtLayout name="auth">
    <!-- Ambient Background Elements -->
    <div class="fixed inset-0 z-0 overflow-hidden pointer-events-none">
      <div
        class="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand/10 blur-[120px] rounded-full"
      />
      <div
        class="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand/5 blur-[120px] rounded-full"
      />
    </div>

    <div class="relative z-10 w-full max-w-2xl">
      <!-- Header Section -->
      <div class="flex flex-col items-center mb-10 text-center">
        <PebbleAppHomeLogo class="h-10 mb-6" />
        <h1
          class="text-4xl md:text-5xl font-black text-white tracking-tighter mb-4"
        >
          Sign up on <span class="text-brand">Pebble</span> as
        </h1>
        <p
          class="text-text-weak-dark text-xs font-bold tracking-[0.2em] uppercase"
        >
          Choose your path on Pebble
        </p>
      </div>

      <!-- Selection Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Viewer Path -->
        <NuxtLink
          to="/get-started/viewer"
          class="glass-heavy border border-white/10 rounded-[2.5rem] p-8 group hover:border-brand/50 transition-all duration-500 relative overflow-hidden"
        >
          <div
            class="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent group-hover:via-brand/50"
          />

          <div
            class="size-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-8 group-hover:bg-brand/10 group-hover:border-brand/20 transition-colors"
          >
            <PlayCircle
              class="w-8 h-8 text-white group-hover:text-brand transition-colors"
            />
          </div>

          <h2 class="text-2xl font-black text-white mb-3 tracking-tight">
            Viewer
          </h2>
          <p class="text-sm text-white/50 leading-relaxed mb-8 font-medium">
            Discover exclusive premieres, support your favorite creators, and
            watch in 4K Ultra HD.
          </p>

          <div
            class="flex items-center gap-2 text-[10px] font-black text-brand tracking-[0.2em] uppercase"
          >
            Start Watching
            <ArrowRight
              class="w-4 h-4 group-hover:translate-x-1 transition-transform"
            />
          </div>
        </NuxtLink>

        <!-- Creator Path -->
        <NuxtLink
          :to="getCreatorJoinUrl"
          target="_blank"
          class="glass-heavy border border-white/10 rounded-[2.5rem] p-8 group hover:border-brand/50 transition-all duration-500 relative overflow-hidden"
        >
          <div
            class="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent group-hover:via-brand/50"
          />

          <div
            class="size-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-8 group-hover:bg-brand/10 group-hover:border-brand/20 transition-colors"
          >
            <Clapperboard
              class="w-8 h-8 text-white group-hover:text-brand transition-colors"
            />
          </div>

          <h2 class="text-2xl font-black text-white mb-3 tracking-tight">
            Creator
          </h2>
          <p class="text-sm text-white/50 leading-relaxed mb-8 font-medium">
            Publish your content, build a global audience, and monetize with
            industry-leading tools.
          </p>

          <div
            class="flex items-center gap-2 text-[10px] font-black text-brand tracking-[0.2em] uppercase"
          >
            Start Creating
            <ArrowRight
              class="w-4 h-4 group-hover:translate-x-1 transition-transform"
            />
          </div>
        </NuxtLink>
      </div>

      <!-- Footer Footer -->
      <p
        class="mt-12 text-center text-xs font-bold text-text-weak-dark tracking-widest"
      >
        Already have an account?
        <NuxtLink
          to="/login"
          class="text-brand hover:text-orange-400 transition-colors ml-2"
        >
          Sign in here
        </NuxtLink>
      </p>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { PlayCircle, Clapperboard, ArrowRight } from "lucide-vue-next";

usePageTitle({
  title: "Get Started",
  description: "Choose your Pebble journey",
});

const { getCreatorJoinUrl } = useGetPebbleCmsUrls();
</script>

<style scoped>
.glass-heavy {
  background: rgba(15, 15, 17, 0.8);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
}

/* Custom shadow to give cards depth */
.glass-heavy:hover {
  box-shadow: 0 25px 50px -12px rgba(249, 115, 22, 0.1);
}
</style>
